import { Injectable, NgZone } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Router } from '@angular/router';
import { StateResetAll } from 'ngxs-reset-plugin';
import { NavController } from '@ionic/angular';
import Bowser from 'bowser';
import { of } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';

import { DeviceState, EnabledFeatureFlag } from '../../models';
import { ConfigProvider } from '../../providers/config.provider';
import { DeviceService } from '../../services/device/device.service';
import {
  CompleteDeviceRegistration,
  GetDeviceRegistrationCode,
  RemovedDevice,
  SetApiUrl,
  SetHardwareDetails,
  UpdatedDevice
} from './app.action';

import { PageConstant } from '../../constants/page.constant';

import { CommonConstant } from '../../constants';
import { DeviceRegistrationPageError } from '../error/error.action';
import { ErrorConstant } from '../../constants/error.constant';
import { SupportLocale } from '@mealsuite/shared-services';

export interface AppStateModel {
  apiBaseUrl: string;
  device: DeviceState;
  locales: SupportLocale[];
  enabled_feature_flags: EnabledFeatureFlag[];
  is_aus?: boolean;
  facility_time_zone?: string;
}

export const APP_STATE_TOKEN = new StateToken<AppStateModel>('app');

@State({
  name: APP_STATE_TOKEN,
  defaults: {
    enabled_feature_flags: [],
    locales: [],
    apiBaseUrl: '',
    device: {
      id: undefined,
      status: undefined,
      name: '',
      facility_id: undefined,
      pos_location_id: undefined,
      operating_system: '',
      hardware_id: undefined,
      device_type: '',
      tenant: 'synergy',
      key: undefined,
      local_os: null,
      uuid: '',
      is_native: false,
      platform: undefined,
      token: undefined,
      locales: []
    }
  }
})
@Injectable({
  providedIn: 'root'
})
export class AppState {
  constructor(
    private readonly ngZone: NgZone,
    private readonly store: Store,
    private readonly deviceService: DeviceService,
    private readonly configProvider: ConfigProvider,
    private readonly navController: NavController,
    private readonly router: Router,
    private readonly translocoService: TranslocoService
  ) {}

  @Selector()
  static facilityTimeZone({ facility_time_zone }: AppStateModel) {
    return facility_time_zone;
  }

  @Selector()
  static getLocales(state: AppStateModel) {
    return state.locales;
  }

  @Selector()
  static isDeviceRegistered(state: AppStateModel) {
    return !!state.device.token;
  }

  //android, ios or web
  @Selector()
  static getPlatform({ device }: AppStateModel) {
    return device.platform;
  }

  @Selector()
  static getDeviceKey({ device }: AppStateModel) {
    return device.key;
  }

  @Selector()
  static getApiBase({ apiBaseUrl }: AppStateModel) {
    return apiBaseUrl;
  }

  //Get Token
  @Selector()
  static getToken({ device }: AppStateModel) {
    return device.token;
  }

  @Selector()
  static getDevice({ device }: AppStateModel) {
    return device;
  }

  @Selector()
  static getFacilityId({ device }: AppStateModel) {
    return device?.facility_id;
  }

  @Selector()
  static getEnabledFeatureFlags({ enabled_feature_flags }: AppStateModel) {
    return enabled_feature_flags;
  }

  @Selector()
  static supportMultilingualApi({ enabled_feature_flags }: AppStateModel): boolean {
    let supportMultilingual = false;

    if (
      enabled_feature_flags?.some((flag) => flag.name === CommonConstant.FEATURE_FLAG.API) &&
      enabled_feature_flags?.some((flag) => flag.name === CommonConstant.FEATURE_FLAG.LANGUAGE_SWITCHER)
    ) {
      supportMultilingual = true;
    }

    return supportMultilingual;
  }

  @Selector()
  static showLanguageSwitcher({ enabled_feature_flags, locales }: AppStateModel): boolean {
    let showLanguageSwitcher = false;

    if (
      enabled_feature_flags?.some((flag) => flag.name === CommonConstant.FEATURE_FLAG.LANGUAGE_SWITCHER) &&
      locales?.length > 1
    ) {
      showLanguageSwitcher = true;
    }

    return showLanguageSwitcher;
  }

  //Sets host and domain data
  @Action(SetApiUrl)
  setApiHost({ patchState }: StateContext<AppStateModel>, { url }: SetApiUrl) {
    patchState({
      apiBaseUrl: url
    });
  }

  //Get code to register device
  @Action(GetDeviceRegistrationCode)
  getCode({ getState, patchState, dispatch }: StateContext<AppStateModel>) {
    const { apiBaseUrl, device: stateDevice } = getState();
    const { uuid: deviceUuid, local_os } = stateDevice;

    return this.deviceService.registerDevice(apiBaseUrl, deviceUuid, local_os?.name || '').pipe(
      tap(({ status, device, locales, enabled_feature_flags, time_zone: facility_time_zone }) => {
        patchState({
          facility_time_zone,
          locales,
          enabled_feature_flags,
          device: {
            ...stateDevice,
            ...device,
            status,
            token: device.key + ':' + deviceUuid
          }
        });

        if (status === 'active') {
          const tenant = device.tenant;

          //Update the provider host if the device is not a synergy device (synergy is set by default)
          if (tenant !== CommonConstant.TENANT_TYPE.SYNERGY) {
            this.configProvider.configHost(tenant);
          }

          dispatch(new CompleteDeviceRegistration());
        }
      }),
      catchError(() => {
        dispatch(new DeviceRegistrationPageError(ErrorConstant.REGISTER_DEVICE_ERROR));
        return of(null);
      })
    );
  }

  @Action(CompleteDeviceRegistration)
  onDeviceRegistrationComplete({ getState }: StateContext<AppStateModel>) {
    const { locales, enabled_feature_flags } = getState();
    if (enabled_feature_flags?.some((flag) => flag.name === 'menuboard_language_switcher')) {
      const primaryLocale = locales?.find((locale) => locale.primary);

      if (primaryLocale) {
        this.translocoService.setActiveLang(primaryLocale.code.split('-')[0]);
      }
    }

    this.ngZone.run(() => this.navController.navigateRoot([PageConstant.MENU_PAGE]));
  }

  //Device was updated in Admin Site
  @Action(UpdatedDevice)
  async updatedDevice(
    { getState, patchState, dispatch }: StateContext<AppStateModel>,
    { deviceActivatedData }: UpdatedDevice
  ) {
    if (!deviceActivatedData) {
      return;
    }

    if (this.router.url.includes(PageConstant.DEVICE_REGISTRATION_PAGE)) {
      const prevDevice = getState().device;
      const { enabled_feature_flags, time_zone: facility_time_zone, locales, is_aus } = deviceActivatedData;

      patchState({
        device: { ...prevDevice, ...deviceActivatedData },
        locales,
        is_aus,
        enabled_feature_flags,
        facility_time_zone
      });
      dispatch(new CompleteDeviceRegistration());
    } else {
      dispatch(new RemovedDevice());
    }
  }

  //Device was removed in the Admin Site
  @Action(RemovedDevice)
  async removedDevice() {
    this.store.dispatch(new StateResetAll());
    this.ngZone.run(() => this.navController.navigateRoot([PageConstant.DEVICE_REGISTRATION_PAGE]));
  }

  //#endregion

  @Action(SetHardwareDetails)
  async setHardwareDetails(ctx: StateContext<AppStateModel>) {
    const ua = window.navigator.userAgent;
    const { os } = Bowser.parse(ua);
    const is_native = Capacitor.isNativePlatform();
    const platform = Capacitor.getPlatform();

    const domain = ctx.getState().apiBaseUrl;

    const device = is_native ? await Device.getId() : null;
    if (is_native && device) {
      // incase we have old device uuid we need to migrate it. Otherwise, let register a new device
      const oldUuid = await this.deviceService.getStorageUuid();
      const nativeUuid = device.uuid + CommonConstant.MENUBOARD_DEVICE_CODE;
      if (nativeUuid && oldUuid && oldUuid !== nativeUuid) {
        // only call migrate with the uuid is different.
        const result = await this.deviceService.migrateDeviceHardwareId(domain, oldUuid, nativeUuid).toPromise();
        if (result) {
          await this.deviceService.setStorageUuid(nativeUuid);
        }
      }
    }
    let { uuid } = is_native ? device || {} : await this.deviceService.getUuid();
    if (is_native) {
      uuid += CommonConstant.MENUBOARD_DEVICE_CODE;
    }
    const state = ctx.getState();
    ctx.patchState({
      device: {
        ...state.device,
        local_os: os,
        uuid,
        is_native,
        platform
      }
    });

    ctx.dispatch(new GetDeviceRegistrationCode());
  }

  //Get support data
  @Selector()
  static getSupportData({ device }: AppStateModel) {
    return {
      os: device.local_os?.name || '',
      uuid: device.uuid,
      facility_id: device.facility_id,
      tenant: device.tenant,
      device_name: device.name,
      device_key: device.key
    };
  }
}
